import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.css';

const Sidebar = ({ projects, currentProject, onProjectChange, onCreateProject, onDeleteProject, onLogout, userPlan, projectLimit, onShowUpgradeModal }) => {
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const location = useLocation();
  
  // Use the effective project limit from ProjectContext/backend
  const effectiveLimit = typeof projectLimit === 'number' ? projectLimit : null;
  const hasRestrictedProjects = effectiveLimit !== null && projects.length > effectiveLimit;

  const menuItems = [
    { 
      path: '/dashboard', 
      label: 'Dashboard', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"></rect>
          <rect x="14" y="3" width="7" height="7"></rect>
          <rect x="14" y="14" width="7" height="7"></rect>
          <rect x="3" y="14" width="7" height="7"></rect>
        </svg>
      )
    },
    { 
      path: '/dashboard/site-audit', 
      label: 'Site Audit', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
      )
    },
    { 
      path: '/dashboard/keyword-analysis', 
      label: 'Keyword Analysis', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
          <path d="M2 17l10 5 10-5"></path>
          <path d="M2 12l10 5 10-5"></path>
        </svg>
      )
    },
    { 
      path: '/dashboard/competitor-analysis', 
      label: 'Competitor Analysis', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      )
    },
    { 
      path: '/dashboard/rank-tracking', 
      label: 'Rank Tracking', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="22 6 13.5 14.5 8.5 9.5 2 16"></polyline>
          <polyline points="16 6 22 6 22 12"></polyline>
        </svg>
      )
    },
    { 
      path: '/dashboard/core-web-vitals', 
      label: 'Core Web Vitals', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
          <path d="M2 17l10 5 10-5"></path>
          <path d="M2 12l10 5 10-5"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      )
    },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.project-dropdown')) {
        setIsProjectDropdownOpen(false);
      }
    };

    if (isProjectDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProjectDropdownOpen]);

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <img src="/logo.png" alt="Parrot SEO" className="sidebar-logo" />
      </div>

      <div className="project-dropdown">
        <button
          className="project-selector"
          onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
        >
          <svg className="project-selector-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
          </svg>
          <span className="project-selector-text">
            {currentProject ? currentProject.name : 'Select Project'}
          </span>
          <svg
            className={`project-selector-arrow ${isProjectDropdownOpen ? 'open' : ''}`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>

        {isProjectDropdownOpen && (
          <div className="project-dropdown-menu">
            {projects.length > 0 ? (
              <>
                {projects.map((project, index) => {
                  // Check if this project is restricted based on effective project limit
                  const isRestricted = hasRestrictedProjects && effectiveLimit !== null && index >= effectiveLimit;
                  
                  return (
                    <div
                      key={project.id}
                      className={`project-dropdown-item-wrapper ${
                        currentProject?.id === project.id ? 'active' : ''
                      } ${isRestricted ? 'restricted' : ''}`}
                    >
                      <button
                        className={`project-dropdown-item ${isRestricted ? 'disabled' : ''}`}
                        onClick={() => {
                          if (isRestricted && onShowUpgradeModal) {
                            onShowUpgradeModal();
                            setIsProjectDropdownOpen(false);
                            return;
                          }
                          onProjectChange(project);
                          setIsProjectDropdownOpen(false);
                        }}
                        disabled={isRestricted}
                        title={isRestricted ? 'Upgrade to access this project' : ''}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                          <span className="project-item-name">{project.name}</span>
                          <span className="project-item-url">{project.url}</span>
                        </div>
                        {isRestricted && (
                          <span className="project-restricted-badge">Upgrade Required</span>
                        )}
                      </button>
                      {!isRestricted && (
                        <button
                          className="project-delete-button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDeleteProject(project);
                            setIsProjectDropdownOpen(false);
                          }}
                          type="button"
                          aria-label={`Delete ${project.name}`}
                          title={`Delete ${project.name}`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
                <div className="project-dropdown-divider"></div>
              </>
            ) : (
              <div className="project-dropdown-empty">
                <p>No projects yet</p>
              </div>
            )}
            <button
              className="project-dropdown-item create-new"
              onClick={() => {
                onCreateProject();
                setIsProjectDropdownOpen(false);
              }}
            >
              <svg className="project-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <span>Create New Project</span>
            </button>
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
          >
            <span className="sidebar-nav-icon">{item.icon}</span>
            <span className="sidebar-nav-label">{item.label}</span>
          </Link>
        ))}
      </nav>

      <div className="sidebar-footer">
        <button
          className="sidebar-upgrade-button"
          onClick={() => window.open('https://parrotseo.com/pricing', '_blank', 'noopener,noreferrer')}
          aria-label="Upgrade to Pro"
          title="Upgrade to Pro"
        >
          <span className="sidebar-nav-label">Upgrade to Pro</span>
        </button>
        {onLogout && (
          <button
            className="sidebar-logout-button"
            onClick={onLogout}
            aria-label="Logout"
            title="Logout"
          >
            <span className="sidebar-nav-label">Logout</span>
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;

